import { Form } from "@inertiajs/react"
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react"
import { type RefObject, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { TagInput } from "@/components/candidates/tag-input"
import InputError from "@/components/input-error"
import { MissionAttachmentsDropzone } from "@/components/missions/mission-attachments-dropzone"
import { MissionBriefVideoField } from "@/components/missions/mission-brief-video-field"
import { MissionCoverImageField } from "@/components/missions/mission-cover-image-field"
import { MissionProductsSection } from "@/components/missions/mission-products-section"
import { MissionTypeSelector } from "@/components/missions/mission-type-selector"
import { TypeSwitchConfirmDialog } from "@/components/missions/type-switch-confirm-dialog"
import { RichTextEditor } from "@/components/rich-text-editor"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useFirstErrorFocus } from "@/hooks/use-first-error-focus"
import { cn } from "@/lib/utils"
import { editMissionPath, missionsPath } from "@/routes"
import {
  type CatalogPickerPayload,
  type MissionContentFormat,
  type MissionFormAttributes,
  type MissionRewardDefaults,
  type MissionType,
  collectErrorMessages,
  pickFirstErrorMessage,
} from "@/types/missions"

/**
 * MissionForm — single shared form for `Brand/Missions/New` and
 * `Brand/Missions/Edit` (UX Spec §2 § New / Edit).
 *
 * **Wire shape (locked with backend, asserted by Vitest canary):**
 *   - Inertia `<Form>` POST `missionsPath()` (New) or PATCH
 *     `missionPath(id)` (Edit) — wired by the parent page.
 *   - All inputs `name="mission[*]"` so Rails reads
 *     `params[:mission][:*]`.
 *   - `commit` discriminator at the form root (NOT under `mission[]`)
 *     valued `"draft"` or `"active"`. The controller maps `commit` →
 *     `status` and **ignores** `params[:mission][:status]` (defence-in-
 *     depth, TRD §2 + QA C35). NO `mission[status]` field.
 *   - `mission[brief]` ships as `JSON.stringify(prosemirror_doc)` via the
 *     `RichTextEditor`'s hidden input.
 *   - Attachments ride as `mission[files][]` carrying Active Storage
 *     `signed_id` strings (one input per blob), emitted by the
 *     `MissionAttachmentsDropzone`.
 *
 * **Conditional fields (PRD §3 + AC):**
 *   - `mission_type === "promotional"` reveals `promo_code` +
 *     `ecommerce_url` (both required for active save).
 *   - `content_format === "other"` reveals `content_format_other`.
 *   - Type switch with type-specific fields populated → confirm dialog
 *     (UX Spec §1 edge flow). Confirm clears `promo_code` +
 *     `ecommerce_url`; shared fields preserved.
 *
 * **NO** `react-hook-form`, `zod`, or shadcn `Form` / `FormField` /
 * `FormItem` (Crew rules). Inertia `<Form>` render-prop owns errors;
 * controlled local state owns the conditional reveal + type-switch
 * dialog state only.
 */
interface MissionFormProps {
  /** Initial values (empty for New, persisted values for Edit). */
  mission: MissionFormAttributes
  /** OKS-456 — resolved brand reward defaults for override placeholders. */
  brandDefaults: MissionRewardDefaults
  /** Form submit endpoint — `missionsPath()` (New) or `missionPath(id)` (Edit). */
  action: string
  /** "post" for New, "patch" for Edit. */
  method: "post" | "patch"
  /** When true, the type selector is locked (post-OKS-30 collabs exist). */
  typeLocked?: boolean
  /**
   * OKS-933 — deferred picker payload, forwarded to the gift fieldset.
   *
   * ⚠️ REQUIRED. The fieldset's copy was optional and this component simply
   * never passed it — which TYPE-CHECKED. The picker rendered its "still
   * resolving" branch forever and nothing could ever be attached. The server
   * prop was correct and the fieldset was correct; only this hand-off was
   * missing, and no compiler, test or lint could see it.
   */
  catalog: CatalogPickerPayload | undefined
  /**
   * OKS-933 (D2) — gates Detach only; attach is never collabs-gated.
   *
   * ⚠️ REQUIRED, and it previously defaulted to `true` one level down — so the
   * unwired path failed OPEN, offering Detach on missions that must not have
   * it. Optional + permissive default is the combination to avoid on a guard.
   */
  canDetachProduct: boolean
  /** Test id for the wrapper. */
  testId?: string
}

const CONTENT_FORMATS: readonly MissionContentFormat[] = [
  "unboxing",
  "tutorial",
  "review",
  "grwm",
  "haul",
  "other",
] as const

export function MissionForm({
  mission,
  brandDefaults,
  action,
  method,
  typeLocked = false,
  catalog,
  canDetachProduct,
  testId = "mission-form",
}: MissionFormProps) {
  // Local controlled state — only for the affordances that drive
  // conditional reveal (type, content_format) + the type-switch dialog.
  // Every other input is uncontrolled with `defaultValue` so Inertia
  // <Form> owns the submit shape directly (Crew render-prop pattern).
  const [type, setType] = useState<MissionType | null>(mission.mission_type)
  const [contentFormat, setContentFormat] =
    useState<MissionContentFormat | null>(mission.content_format)
  const [pendingType, setPendingType] = useState<MissionType | null>(null)
  // Tracks whether the user has typed into a type-specific field — drives
  // the "switching clears these fields" confirm dialog.
  const [hasTypeSpecificData, setHasTypeSpecificData] = useState(
    Boolean(mission.promo_code ?? mission.ecommerce_url),
  )
  // OKS-495 — same dirty-tracking for the gift product, so a switch AWAY
  // from a gifting type (drop/promotional → open) prompts before discarding.
  const [hasProductData, setHasProductData] = useState(
    mission.mission_products.length > 0,
  )
  // Bumping this key forces the conditional `<input>`s to re-mount with
  // empty defaultValues after a confirmed type switch — the simplest way
  // to clear an uncontrolled input.
  const [typeSpecificResetKey, setTypeSpecificResetKey] = useState(0)
  // OKS-614 — Base UI's controlled radio leaves the rejected item's hidden
  // <input> checked after a cancelled switch, so re-clicking the same card
  // (label → hidden input) fires no native change and the confirm dialog
  // never re-opens. Bumping this key remounts the selector so its hidden
  // inputs reconcile with the controlled `type`.
  const [typeSelectorResetKey, setTypeSelectorResetKey] = useState(0)
  // Anchors `useFirstErrorFocus` — points at the wrapper so the hook
  // can scan every form input regardless of nesting.
  const formRef = useRef<HTMLDivElement>(null)

  // OKS-495 — Confirm a type switch when the OUTGOING type carries data the
  // INCOMING type won't preserve:
  //   - leaving `promotional` with promo data (→ drop or open), OR
  //   - leaving a gifting type for the non-gifting `open` with product data.
  // (drop → promotional keeps the product fieldset, so no prompt there.)
  function requestTypeChange(next: MissionType) {
    if (typeLocked) return
    if (next === type) return

    const losingPromo =
      type === "promotional" && next !== "promotional" && hasTypeSpecificData
    const losingProduct =
      isGiftingType(type) && !isGiftingType(next) && hasProductData

    if (losingPromo || losingProduct) {
      setPendingType(next)
      return
    }
    setType(next)
  }

  function confirmTypeChange() {
    if (pendingType === null) return
    setType(pendingType)
    setHasTypeSpecificData(false)
    setHasProductData(false)
    setTypeSpecificResetKey((k) => k + 1)
    setPendingType(null)
  }

  return (
    <div ref={formRef} data-testid={testId} className="flex flex-col gap-6">
      <Form
        action={action}
        method={method}
        options={{ preserveScroll: true }}
        className="flex flex-col gap-6"
        data-testid="mission-form-element"
      >
        {({ processing, errors }) => (
          <MissionFormBody
            catalog={catalog}
            canDetachProduct={canDetachProduct}
            errors={errors as Record<string, unknown>}
            processing={processing}
            formRef={formRef}
            mission={mission}
            brandDefaults={brandDefaults}
            type={type}
            typeLocked={typeLocked}
            requestTypeChange={requestTypeChange}
            contentFormat={contentFormat}
            setContentFormat={setContentFormat}
            setHasTypeSpecificData={setHasTypeSpecificData}
            setHasProductData={setHasProductData}
            typeSpecificResetKey={typeSpecificResetKey}
            typeSelectorResetKey={typeSelectorResetKey}
          />
        )}
      </Form>

      <TypeSwitchConfirmDialog
        pendingType={pendingType}
        onConfirm={confirmTypeChange}
        onCancel={() => {
          setPendingType(null)
          // Remount the selector so Base UI's rejected radio input resets
          // to the still-current `type` (OKS-614 — see reset-key comment).
          setTypeSelectorResetKey((k) => k + 1)
        }}
      />
    </div>
  )
}

interface MissionFormBodyProps {
  /**
   * OKS-933 — ⚠️ the hand-off that was MISSING and shipped a dead picker.
   *
   * The chain is page → MissionForm → MissionFormBody → MissionProductFieldset,
   * FOUR hops, and the fieldset's prop was optional — so dropping it anywhere
   * along the way type-checked and the picker rendered "still resolving"
   * forever. Required at every hop now, so the compiler enumerates the chain.
   */
  catalog: CatalogPickerPayload | undefined
  /** OKS-933 (D2) — gates Detach only. Required: its default failed OPEN. */
  canDetachProduct: boolean
  errors: Record<string, unknown>
  processing: boolean
  formRef: RefObject<HTMLDivElement | null>
  mission: MissionFormAttributes
  brandDefaults: MissionRewardDefaults
  type: MissionType | null
  typeLocked: boolean
  requestTypeChange: (next: MissionType) => void
  contentFormat: MissionContentFormat | null
  setContentFormat: (next: MissionContentFormat) => void
  setHasTypeSpecificData: (next: boolean) => void
  setHasProductData: (next: boolean) => void
  typeSpecificResetKey: number
  typeSelectorResetKey: number
}

// OKS-495 — Gifting mission types reveal the gift-product fieldset.
function isGiftingType(type: MissionType | null): boolean {
  return type === "drop" || type === "promotional"
}

// Inner component so `useFirstErrorFocus` can run at component-body
// scope — the Inertia `<Form>` render-prop is a callback, not a
// component, so hooks can't live in there.
function MissionFormBody({
  errors,
  processing,
  formRef,
  mission,
  brandDefaults,
  type,
  typeLocked,
  requestTypeChange,
  contentFormat,
  setContentFormat,
  setHasTypeSpecificData,
  setHasProductData,
  typeSpecificResetKey,
  typeSelectorResetKey,
  catalog,
  canDetachProduct,
}: MissionFormBodyProps) {
  const { t } = useTranslation()
  useFirstErrorFocus(errors, formRef)

  const errorCount = Object.keys(errors).length
  const showErrorAlert = errorCount > 0

  return (
    <>
      {showErrorAlert && (
        <Alert
          variant="destructive"
          role="alert"
          data-testid="mission-form-error-summary"
        >
          <AlertTitle>{t("missions.form.errors_alert.title")}</AlertTitle>
          <AlertDescription>
            {errorCount === 1
              ? t("missions.form.errors_alert.count_one")
              : t("missions.form.errors_alert.count_other", {
                  count: errorCount,
                })}
          </AlertDescription>
        </Alert>
      )}

      <MissionTypeSelector
        key={typeSelectorResetKey}
        value={type}
        onChange={requestTypeChange}
        locked={typeLocked}
      />
      <FieldError name="mission_type" error={errors.mission_type as never} />

      {type !== null && (
        <>
          <FormField
            id="mission-title"
            name="mission[title]"
            label={t("missions.form.title.label")}
            helper={t("missions.form.title.helper")}
            placeholder={t("missions.form.title.placeholder")}
            defaultValue={mission.title}
            error={pickFirstErrorMessage(errors.title as never)}
            required
          />

          <div className="flex flex-col gap-2">
            <Label htmlFor="mission-brief">
              {t("missions.form.brief.label")}
            </Label>
            <RichTextEditor
              id="mission-brief"
              name="mission[brief]"
              defaultValue={mission.brief}
              placeholder={t("missions.form.brief.placeholder")}
              ariaLabel={t("missions.form.brief.label")}
              invalid={Boolean(errors.brief)}
              describedBy="mission-brief-helper mission-brief-error"
              testId="mission-brief-editor"
            />
            <p
              id="mission-brief-helper"
              className="text-muted-foreground text-xs"
            >
              {t("missions.form.brief.helper")}
            </p>
            <InputError
              id="mission-brief-error"
              data-testid="mission-form-error-brief"
              messages={
                errors.brief
                  ? [pickFirstErrorMessage(errors.brief as never) ?? ""]
                  : undefined
              }
            />
          </div>

          <FormField
            id="mission-product-info"
            name="mission[product_info]"
            label={t("missions.form.product_info.label")}
            placeholder={t("missions.form.product_info.placeholder")}
            defaultValue={mission.product_info ?? ""}
            error={pickFirstErrorMessage(errors.product_info as never)}
          />

          <ContentFormatPicker
            value={contentFormat}
            onChange={setContentFormat}
            error={pickFirstErrorMessage(errors.content_format as never)}
          />
          {contentFormat === "other" && (
            <FormField
              id="mission-content-format-other"
              name="mission[content_format_other]"
              label={t("missions.form.content_format_other.label")}
              placeholder={t("missions.form.content_format_other.placeholder")}
              defaultValue={mission.content_format_other ?? ""}
              error={pickFirstErrorMessage(
                errors.content_format_other as never,
              )}
              required
            />
          )}

          <FormField
            id="mission-deadline"
            name="mission[deadline]"
            type="date"
            label={t("missions.form.deadline.label")}
            helper={t("missions.form.deadline.helper")}
            defaultValue={mission.deadline ?? ""}
            error={pickFirstErrorMessage(errors.deadline as never)}
            required
          />

          <div className="flex flex-col gap-2">
            <Label htmlFor="mission-hashtags">
              {t("missions.form.hashtags.label")}
            </Label>
            <TagInput
              id="mission-hashtags"
              name="mission[hashtags]"
              defaultValue={mission.hashtags ? mission.hashtags.split(";") : []}
              placeholder={t("missions.form.hashtags.placeholder")}
              describedBy="mission-hashtags-helper"
              testId="mission-input-hashtags"
            />
            <p
              id="mission-hashtags-helper"
              className="text-muted-foreground text-xs"
            >
              {t("missions.form.hashtags.helper")}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="mission-mentions">
              {t("missions.form.mentions.label")}
            </Label>
            <TagInput
              id="mission-mentions"
              name="mission[mentions]"
              defaultValue={mission.mentions ? mission.mentions.split(";") : []}
              placeholder={t("missions.form.mentions.placeholder")}
              describedBy="mission-mentions-helper"
              testId="mission-input-mentions"
            />
            <p
              id="mission-mentions-helper"
              className="text-muted-foreground text-xs"
            >
              {t("missions.form.mentions.helper")}
            </p>
          </div>

          <fieldset
            className="flex flex-col gap-4 rounded-md border p-4"
            data-testid="mission-media-group"
          >
            <legend className="px-1 text-sm font-medium">
              {t("missions.media.group_label")}
            </legend>
            <p className="text-muted-foreground text-xs">
              {t("missions.media.group_helper")}
            </p>
            <MissionCoverImageField
              currentUrl={mission.cover_image_display_url}
              hasCoverImage={mission.has_cover_image}
              isLegacy={mission.cover_image_is_legacy}
              error={
                pickFirstErrorMessage(errors.cover_image as never) ?? undefined
              }
            />
            <div className="flex flex-col gap-2">
              <Label>{t("missions.media.video_label")}</Label>
              <MissionBriefVideoField
                initialFilename={mission.brief_video_filename}
                initialSignedId={mission.brief_video_signed_id}
                errorMessage={
                  pickFirstErrorMessage(errors.brief_video as never) ??
                  undefined
                }
              />
            </div>
          </fieldset>

          <div className="flex flex-col gap-2">
            <Label htmlFor="mission-attachments-dropzone">
              {t("missions.form.attachments.label")}
            </Label>
            <MissionAttachmentsDropzone
              defaultValue={mission.attachments}
              errors={collectErrorMessages(errors.files as never)}
            />
          </div>

          <RewardOverridesSection
            mission={mission}
            brandDefaults={brandDefaults}
            errors={errors}
          />

          {/* OKS-495 — Gift-product fieldset, revealed for gifting types
              (drop + promotional), sitting alongside the conditional
              promotional fields. Re-mounts on a confirmed type switch
              (key bump) to clear the uncontrolled inputs. */}
          {isGiftingType(type) && (
            <MissionProductsSection
              key={`gift-products-${typeSpecificResetKey}`}
              products={mission.mission_products}
              errors={errors}
              onDirty={() => setHasProductData(true)}
              catalog={catalog}
              canDetach={canDetachProduct}
            />
          )}

          {type === "promotional" && (
            <div
              key={`promotional-${typeSpecificResetKey}`}
              className="flex flex-col gap-4 rounded-md border border-dashed p-4"
              data-testid="mission-promotional-fields"
            >
              <FormField
                id="mission-promo-code"
                name="mission[promo_code]"
                label={t("missions.form.promo_code.label")}
                placeholder={t("missions.form.promo_code.placeholder")}
                defaultValue={mission.promo_code ?? ""}
                error={pickFirstErrorMessage(errors.promo_code as never)}
                required
                onChange={() => setHasTypeSpecificData(true)}
              />
              <FormField
                id="mission-ecommerce-url"
                name="mission[ecommerce_url]"
                type="url"
                label={t("missions.form.ecommerce_url.label")}
                placeholder={t("missions.form.ecommerce_url.placeholder")}
                helper={t("missions.form.ecommerce_url.helper")}
                defaultValue={mission.ecommerce_url ?? ""}
                error={pickFirstErrorMessage(errors.ecommerce_url as never)}
                required
                onChange={() => setHasTypeSpecificData(true)}
              />
            </div>
          )}

          <FormFooter
            processing={processing}
            cancelHref={
              mission.id ? editMissionPath(mission.id) : missionsPath()
            }
          />
        </>
      )}
    </>
  )
}

export interface FormFieldProps {
  id: string
  name: string
  label: string
  placeholder?: string
  helper?: string
  type?: string
  inputMode?: "text" | "numeric" | "email" | "url"
  min?: number
  step?: number
  defaultValue?: string
  error: string | null
  required?: boolean
  /**
   * OKS-935 — optional unit rendered beside the input (UX §2's « jours »).
   * Additive and optional: every existing caller renders identically without
   * it. Kept OUT of the label so the unit is a sibling of the control rather
   * than part of its accessible name.
   */
  suffix?: string
  onChange?: () => void
  /**
   * OKS-1000 — imperative access to the input, so a caller can PREFILL it
   * without making it controlled.
   *
   * ⚠️ Additive and optional. Absent, the rendered tree and the `defaultValue`
   * path are byte-identical, so no existing caller changes. The gift fieldset
   * needs to write a catalogue price into the value field on pick while the
   * brand can still type over it freely; a controlled binding would stomp
   * typing, and changing `defaultValue` on a mounted input is a silent no-op.
   */
  inputRef?: React.Ref<HTMLInputElement>
  /**
   * OKS-1000 — the input's current value on every keystroke. `onChange` above
   * is intentionally argument-less (its callers only need "something changed"),
   * and a hint that must appear and disappear as the field empties and fills
   * needs the value itself.
   */
  onValueChange?: (value: string) => void
  /**
   * OKS-1000 — id(s) of description text rendered OUTSIDE this component, JOINED
   * with the helper and error ids rather than replacing them.
   *
   * ⚠️ The join is the load-bearing part. Three contributors can coexist
   * (helper, error, and a caller's own description), and assigning instead of
   * joining silently drops whichever the caller did not know about — a
   * regression a presence-only assertion cannot see, because
   * `aria-describedby` would still be non-empty and still point at something
   * real.
   *
   * The gift fieldset's value hint uses this: the hint is a sibling `<p>` it
   * owns and renders conditionally, so it cannot be expressed as `helper`
   * (which this component renders itself and which carries no variant).
   */
  describedBy?: string
}

export function FormField({
  id,
  name,
  label,
  placeholder,
  helper,
  type = "text",
  inputMode,
  min,
  step,
  defaultValue,
  error,
  required,
  suffix,
  onChange,
  inputRef,
  onValueChange,
  describedBy: externalDescribedBy,
}: FormFieldProps) {
  const helperId = helper ? `${id}-helper` : undefined
  const errorId = error ? `${id}-error` : undefined
  // ⚠️ Order is deliberate and not alphabetical: helper (standing guidance),
  // then error (the urgent one), then the caller's own. Screen readers announce
  // these in sequence.
  const describedBy =
    [helperId, errorId, externalDescribedBy].filter(Boolean).join(" ") ||
    undefined
  // One definition, rendered bare or wrapped. `w-24` applies ONLY on the
  // suffixed path so no existing caller's width changes.
  const inputEl = (
    <Input
      id={id}
      name={name}
      type={type}
      inputMode={inputMode}
      min={min}
      step={step}
      ref={inputRef}
      defaultValue={defaultValue}
      placeholder={placeholder}
      required={required}
      aria-required={required ? "true" : undefined}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={describedBy}
      data-testid={`mission-input-${name.replace(/^mission\[(.+)\]$/, "$1")}`}
      onChange={
        onChange || onValueChange
          ? (event) => {
              onChange?.()
              onValueChange?.(event.target.value)
            }
          : undefined
      }
      className={suffix ? "w-24" : undefined}
    />
  )
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      {/*
        ⚠️ The wrapper is CONDITIONAL on `suffix`, and that is load-bearing.
        Wrapping unconditionally would change the DOM for EVERY FormField in
        the mission form: the bare `<Input>` is a direct flex-item of the
        `flex-col` parent today, and nesting it inside `flex flex-wrap
        items-center` re-parents its sizing. Vitest cannot see that (jsdom does
        no layout), so a green suite would not have caught it — the blast
        radius would have been every field on a shared surface, for a
        one-field feature. Without `suffix` the tree below is byte-identical
        to pre-OKS-935.
      */}
      {suffix ? (
        <div className="flex flex-wrap items-center gap-2">
          {inputEl}
          <span
            className="text-muted-foreground text-xs"
            data-testid={`mission-input-suffix-${name.replace(/^mission\[(.+)\]$/, "$1")}`}
          >
            {suffix}
          </span>
        </div>
      ) : (
        inputEl
      )}
      {helper && (
        <p id={helperId} className="text-muted-foreground text-xs">
          {helper}
        </p>
      )}
      <InputError
        id={errorId}
        data-testid={`mission-form-error-${name.replace(/^mission\[(.+)\]$/, "$1")}`}
        messages={error ? [error] : undefined}
      />
    </div>
  )
}

function FieldError({
  name,
  error,
}: {
  name: string
  error: string | undefined
}) {
  if (!error) return null
  return (
    <InputError
      id={`mission-${name}-error`}
      data-testid={`mission-form-error-${name}`}
      messages={[error]}
    />
  )
}

/**
 * OKS-456 — Per-mission reward overrides, grouped under a collapsed-by-
 * default "Rewards (advanced)" section (UX Spec §2.B). Each of the five
 * components is optional: a blank field inherits the brand default
 * (stored as NULL); a typed `0` is a distinct zero payout. Placeholders
 * + helpers surface the resolved brand default so the inherit behaviour
 * is explicit (UX-OQ "blank = inherit communicated in text").
 *
 * Replaces the deprecated `mission[xp_reward]` field (severed from
 * payout). NO shadcn Form / RHF / zod — reuses the local Inertia-native
 * `FormField` + render-prop errors.
 *
 * The section auto-opens when any override field has a server error so
 * `useFirstErrorFocus` can reach the (otherwise unmounted) field.
 */
const REWARD_OVERRIDE_FIELDS: readonly {
  key: keyof MissionRewardDefaults
  i18nKey: string
  unit: "xp" | "coins"
  note?: boolean
}[] = [
  { key: "xp_base_override", i18nKey: "xp_base", unit: "xp" },
  { key: "xp_on_time_bonus_override", i18nKey: "xp_on_time", unit: "xp" },
  {
    key: "xp_engagement_bonus_override",
    i18nKey: "xp_engagement",
    unit: "xp",
    note: true,
  },
  { key: "coins_base_override", i18nKey: "coins_base", unit: "coins" },
  {
    key: "coins_on_time_bonus_override",
    i18nKey: "coins_on_time",
    unit: "coins",
  },
] as const

function RewardOverridesSection({
  mission,
  brandDefaults,
  errors,
}: {
  mission: MissionFormAttributes
  brandDefaults: MissionRewardDefaults
  errors: Record<string, unknown>
}) {
  const { t } = useTranslation()

  const overrideCount = REWARD_OVERRIDE_FIELDS.filter(
    ({ key }) => mission[key] != null,
  ).length
  const hasErrors = REWARD_OVERRIDE_FIELDS.some(({ key }) =>
    Boolean(errors[key]),
  )

  // `userOpen` is null until the user manually toggles the section; the
  // effective open state then derives from errors so a post-submit
  // re-render with an override error auto-opens (so the errored field
  // mounts and `useFirstErrorFocus` can reach it) WITHOUT a setState-in-
  // effect. Once the user toggles, their choice wins.
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const open = userOpen ?? hasErrors

  const subtitle =
    overrideCount > 0
      ? t("missions.form.rewards.subtitle_custom", { count: overrideCount })
      : t("missions.form.rewards.subtitle_inherit")

  const xpFields = REWARD_OVERRIDE_FIELDS.filter((f) => f.unit === "xp")
  const coinFields = REWARD_OVERRIDE_FIELDS.filter((f) => f.unit === "coins")

  const renderField = (field: (typeof REWARD_OVERRIDE_FIELDS)[number]) => {
    const defaultValue = brandDefaults[field.key]
    const helperBase = t("missions.form.rewards.brand_default", {
      value: defaultValue,
    })
    const helper = field.note
      ? `${helperBase} ${t("missions.form.rewards.engagement_note")}`
      : helperBase
    const current = mission[field.key]
    return (
      <FormField
        key={field.key}
        id={`mission-${field.key.replace(/_/g, "-")}`}
        name={`mission[${field.key}]`}
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        label={t(`missions.form.rewards.fields.${field.i18nKey}.label`)}
        placeholder={t("missions.form.rewards.inherits", {
          value: defaultValue,
        })}
        helper={helper}
        defaultValue={current == null ? "" : String(current)}
        error={pickFirstErrorMessage(errors[field.key] as never)}
      />
    )
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setUserOpen}
      className="rounded-md border p-4"
      data-testid="mission-rewards-overrides"
    >
      <CollapsibleTrigger
        render={
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-left"
            data-testid="mission-rewards-overrides-trigger"
          />
        }
      >
        <span className="flex flex-col">
          <span className="text-sm font-medium">
            {t("missions.form.rewards.title")}
          </span>
          <span
            className="text-muted-foreground text-xs"
            data-testid="mission-rewards-overrides-subtitle"
          >
            {subtitle}
          </span>
        </span>
        {open ? (
          <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4">
        <p className="text-muted-foreground mb-4 text-xs">
          {t("missions.form.rewards.helper")}
        </p>
        <div className="flex flex-col gap-6">
          <fieldset className="flex flex-col gap-4">
            <legend className="text-sm font-medium">
              {t("missions.form.rewards.xp_legend")}
            </legend>
            {xpFields.map(renderField)}
          </fieldset>
          <fieldset className="flex flex-col gap-4">
            <legend className="text-sm font-medium">
              {t("missions.form.rewards.coins_legend")}
            </legend>
            {coinFields.map(renderField)}
          </fieldset>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

interface ContentFormatPickerProps {
  value: MissionContentFormat | null
  onChange: (next: MissionContentFormat) => void
  error: string | null
}

function ContentFormatPicker({
  value,
  onChange,
  error,
}: ContentFormatPickerProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="mission-content-format">
        {t("missions.form.content_format.label")}
      </Label>
      <RadioGroup
        id="mission-content-format"
        name="mission[content_format]"
        value={value ?? undefined}
        onValueChange={(next) => onChange(next as MissionContentFormat)}
        className="flex flex-wrap gap-2"
        data-testid="mission-content-format"
      >
        {CONTENT_FORMATS.map((fmt) => (
          <label
            key={fmt}
            htmlFor={`content-format-${fmt}`}
            className={cn(
              "border-input flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm",
              value === fmt && "border-primary bg-primary/5",
            )}
            data-testid={`content-format-${fmt}`}
            data-selected={value === fmt ? "true" : "false"}
          >
            <RadioGroupItem
              id={`content-format-${fmt}`}
              value={fmt}
              // First focusable item carries the synthetic-anchor so
              // `useFirstErrorFocus` lands here for `content_format`
              // errors — the Radix BubbleInputs holding `name=` are
              // `tabindex="-1"` and not focus targets.
              data-error-anchor={
                fmt === CONTENT_FORMATS[0] ? "content_format" : undefined
              }
            />
            <span>{t(`missions.form.content_format.options.${fmt}`)}</span>
          </label>
        ))}
      </RadioGroup>
      <InputError
        data-testid="mission-form-error-content_format"
        messages={error ? [error] : undefined}
      />
    </div>
  )
}

interface FormFooterProps {
  processing: boolean
  cancelHref: string
}

function FormFooter({ processing, cancelHref }: FormFooterProps) {
  const { t } = useTranslation()
  return (
    <div className="border-border flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
      <Button
        type="button"
        variant="ghost"
        asChild
        disabled={processing}
        data-testid="mission-form-cancel"
      >
        <a href={cancelHref}>{t("missions.form.type_switch_confirm.cancel")}</a>
      </Button>
      <Button
        type="submit"
        name="commit"
        value="draft"
        variant="outline"
        disabled={processing}
        // PRD §5: a draft saves with title + type only; everything else
        // can be empty. The required `<Input>` fields (deadline,
        // content_format_other when format=other, promo_code +
        // ecommerce_url when type=promotional) carry HTML5 `required`
        // for the active-save path's a11y / asterisk + browser-level
        // guard. `formNoValidate` makes THIS submit path skip the
        // browser's validation pass so an empty deadline (etc.) doesn't
        // block a draft save. Server's active-only validators still gate
        // a future draft → active transition.
        formNoValidate
        data-testid="mission-form-submit-draft"
      >
        {processing && (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        )}
        {t("missions.form.submit.save_draft")}
      </Button>
      <Button
        type="submit"
        name="commit"
        value="active"
        disabled={processing}
        data-testid="mission-form-submit-active"
      >
        {processing && (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        )}
        {t("missions.form.submit.save_active")}
      </Button>
    </div>
  )
}
